import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, RotateCcw, Save, Languages } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { setAutoOrg } from "../lib/autoTranslate";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const LANGS = [["en", "English"], ["ur", "اردو"]];

const Cell = ({ item, lng, orgId, onSaved }) => {
  const custom = item.custom[lng];
  const [v, setV] = useState(custom ?? item.auto[lng] ?? "");
  const [busy, setBusy] = useState(false);
  const base = custom ?? item.auto[lng] ?? "";
  const save = async (out) => {
    setBusy(true);
    try { await api.i18nOverride({ src: item.src, lang: lng, out }); toast.success(out ? "حُفظت الترجمة" : "أُرجعت للترجمة التلقائية"); setAutoOrg(orgId); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  return (
    <td className="px-2 py-2 align-top">
      <div className="flex items-center gap-1">
        <Input value={v} onChange={(e) => setV(e.target.value)} dir={lng === "en" ? "ltr" : "rtl"} style={{ letterSpacing: 0 }}
          placeholder="لم تُترجم بعد — اكتب ترجمتك" className={`h-9 text-xs ${custom != null ? "border-emerald-400 bg-emerald-50/50" : ""}`} data-testid={`tr-input-${lng}`} />
        {v.trim() && v !== base && <button onClick={() => save(v)} disabled={busy} className="rounded-md p-1.5 text-emerald-700 hover:bg-emerald-50" title="حفظ" data-testid={`tr-save-${lng}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}</button>}
        {custom != null && <button onClick={() => save("")} disabled={busy} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" title="إرجاع للتلقائية" data-testid={`tr-revert-${lng}`}>
          <RotateCcw className="h-4 w-4" /></button>}
      </div>
      {custom != null && item.auto[lng] && <p className="mt-0.5 truncate text-[10px] text-slate-400" dir="auto" data-no-translate title={item.auto[lng]}>{item.auto[lng]}</p>}
    </td>
  );
};

export const TranslationReview = ({ open, onOpenChange, orgId }) => {
  const { dir } = useLang();
  const [q, setQ] = useState("");
  const [edited, setEdited] = useState(false);
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const load = useCallback(() => api.i18nEntries({ q, edited, page }).then(setD).catch(() => toast.error("تعذّر التحميل")), [q, edited, page]);
  useEffect(() => { if (!open) return undefined; const id = setTimeout(load, 300); return () => clearTimeout(id); }, [open, load]);
  useEffect(() => { setPage(1); }, [q, edited]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} className="max-h-[90vh] overflow-y-auto sm:max-w-5xl" data-testid="translation-review">
        <DialogHeader className="text-start">
          <DialogTitle className="flex items-center gap-2 font-heading"><Languages className="h-5 w-5 text-blue-600" /> مراجعة الترجمات</DialogTitle>
          <DialogDescription className="text-start text-xs">عدّل أي ترجمة تلقائية لتناسب أسلوب شركتك — التعديل يظهر لموظفيك ومناديبك فقط. حافظ على الرموز <span dir="ltr" className="font-mono" data-no-translate>{"{0} {1}"}</span> كما هي، فهي تُستبدل بالأرقام.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1"><Search className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في النص العربي أو الترجمة" className="h-9 pr-8" data-testid="tr-search" /></div>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={edited} onChange={(e) => setEdited(e.target.checked)} data-testid="tr-only-edited" /> المعدّلة فقط {d ? `(${d.edited})` : ""}</label>
        </div>
        {!d ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[760px] text-xs">
                <thead className="bg-slate-50 text-slate-600"><tr><th className="w-1/3 px-3 py-2 text-start">النص العربي</th>
                  {LANGS.map(([k, l]) => <th key={k} className="px-2 py-2 text-start" style={{ letterSpacing: 0 }} data-no-translate>{l}</th>)}</tr></thead>
                <tbody>
                  {d.items.map((it) => (
                    <tr key={it.src} className="border-t border-slate-100" data-testid="tr-row">
                      <td className="px-3 py-2 align-top font-semibold text-slate-800 tracking-normal" style={{ letterSpacing: 0 }} dir="rtl" data-no-translate>{it.src}</td>
                      {LANGS.map(([k]) => <Cell key={k + it.src + (it.custom[k] || "")} item={it} lng={k} orgId={orgId} onSaved={load} />)}
                    </tr>))}
                  {!d.items.length && <tr><td colSpan={3} className="py-8 text-center text-slate-400">لا توجد نصوص</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span data-testid="tr-total">{d.total} نص</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="tr-prev">السابق</Button>
                <span>{page} / {d.pages}</span>
                <Button size="sm" variant="outline" disabled={page >= d.pages} onClick={() => setPage(page + 1)} data-testid="tr-next">التالي</Button>
              </div>
            </div>
          </>)}
      </DialogContent>
    </Dialog>
  );
};
